import { forwardRef } from 'react'

import { cx, cva, RecipeVariantProps, sva } from 'styled-system/css'
import { Box, BoxProps } from 'styled-system/jsx'

/**
 * Layout recipe for HBox, VBox, and Pane primitives.
 */
const layoutRecipe = sva({
  className: 'layoutComponent',
  slots: ['hbox', 'vbox', 'pane'],
  base: {
    hbox: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      height: 'full',
      maxHeight: 'full',
      maxWidth: 'full',
      minWidth: 0,
      width: 'full',
      '& > .layoutComponent__pane': {
        height: 'full',
        maxWidth: 'full',
      },
    },
    vbox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      height: 'full',
      maxHeight: 'full',
      maxWidth: 'full',
      minWidth: 0,
      width: 'full',
      '& > .layoutComponent__pane': {
        maxHeight: 'full',
        width: 'full',
      },
    },
    pane: {},
  },
})

const paneRecipe = cva({
  base: {
    flexGrow: 0,
    flexShrink: 0,
  },
  variants: {
    stretch: {
      true: {
        flexGrow: 1,
        flexShrink: 1,
        overflow: 'auto',
      },
    },
    scrollable: {
      true: {
        overflow: 'auto',
      },
    },
  },
})

type PaneVariants = RecipeVariantProps<typeof paneRecipe>

const layoutRecipeClassnames = layoutRecipe()
/**
 * HBox is wrapper for horizontal layout.
 * Fill all space available in the parent container.
 */
export const HBox = forwardRef<HTMLDivElement, BoxProps>(
  ({ className, ...props }, ref) => (
    <Box
      className={cx(layoutRecipeClassnames.hbox, className)}
      {...props}
      ref={ref}
    />
  ),
)

/**
 * VBox is wrapper for vertical layout.
 * Fill all space available in the parent container.
 */
export const VBox = forwardRef<HTMLDivElement, BoxProps>(
  ({ className, ...props }, ref) => (
    <Box
      className={cx(layoutRecipeClassnames.vbox, className)}
      {...props}
      ref={ref}
    />
  ),
)

/**
 * Pane is wrapper for layout element inside HBox or VBox.
 */
export const Pane = forwardRef<HTMLDivElement, BoxProps & PaneVariants>(
  ({ className, ...props }, ref) => {
    const [variantProps, restProps] = paneRecipe.splitVariantProps(props)
    const classes = paneRecipe(variantProps)

    return (
      <Box
        ref={ref}
        className={cx(layoutRecipeClassnames.pane, classes, className)}
        {...restProps}
      />
    )
  },
)
